import React from 'react';
import { ArrowLeft, UploadCloud } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatBytes, formatDurationSeconds } from '@/config/adminVideoUpload';

function Row({ label, children }) {
  return (
    <div className="flex flex-col gap-1 py-3 sm:flex-row sm:gap-4">
      <dt className="w-full flex-shrink-0 text-xs font-medium text-[#8b6f47] sm:w-40 sm:pt-0.5">
        {label}
      </dt>
      <dd className="min-w-0 break-words text-sm text-[#2d1810]">{children}</dd>
    </div>
  );
}

function FileRow({ label, file }) {
  return (
    <Row label={label}>
      {file ? (
        <>
          <span className="break-all">{file.name}</span>
          <span className="ml-2 text-xs text-[#8b6f47]">{formatBytes(file.size)}</span>
        </>
      ) : (
        <span className="text-[#8b6f47]">なし</span>
      )}
    </Row>
  );
}

/** 確認画面。ここで「アップロード」を押すまでS3・DBには一切触らない。 */
export default function AdminVideoCreateConfirm({
  form,
  thumbnailPreviewUrl,
  categoryName,
  onBack,
  onConfirm,
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-[#e8dccd] bg-white p-5">
        <h2 className="mb-1 text-sm font-semibold text-[#2d1810]">入力内容の確認</h2>
        <p className="mb-4 text-xs text-[#6b5540]">
          「アップロード」を押すと、S3への格納・動画変換・videos テーブルへの登録を実行します。
        </p>

        {thumbnailPreviewUrl && (
          <div className="mb-4 aspect-video w-full max-w-sm overflow-hidden rounded-lg border border-[#e8dccd] bg-[#f5ede3]">
            <img src={thumbnailPreviewUrl} alt="" className="h-full w-full object-cover" />
          </div>
        )}

        <dl className="divide-y divide-[#f0e6d8]">
          <FileRow label="動画ファイル" file={form.videoFile} />
          <FileRow label="サムネイル画像" file={form.thumbnailFile} />
          <FileRow label="配布資料" file={form.materialFile} />
          <Row label="動画タイトル">{String(form.title).trim()}</Row>
          <Row label="カテゴリ">{categoryName ?? '-'}</Row>
          <Row label="アンケートURL">
            <span className="break-all">{String(form.questionnaireUrl).trim()}</span>
          </Row>
          <Row label="動画時間">
            {form.durationSeconds ? (
              formatDurationSeconds(form.durationSeconds)
            ) : (
              <span className="text-[#8b6f47]">変換後に自動設定</span>
            )}
          </Row>
        </dl>
      </div>

      <div className="rounded-2xl border border-[#e8dccd] bg-white p-5">
        <h2 className="mb-3 text-sm font-semibold text-[#2d1810]">アップロード後に自動実行する処理</h2>
        <ol className="list-inside list-decimal space-y-1 text-xs text-[#6b5540]">
          <li>動画識別子（カテゴリslug + 3桁連番）の生成</li>
          <li>元動画から掲載用のMP4を生成（16fps・200〜300MB目安）</li>
          <li>各ファイルを識別子で命名し、videos / thumbs / study へ格納</li>
          <li>videos テーブルへレコードを追加</li>
          <li>共有用URLの生成</li>
        </ol>
      </div>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="gap-2 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
        >
          <ArrowLeft className="h-4 w-4" />
          戻る
        </Button>
        <Button
          type="button"
          onClick={onConfirm}
          className="gap-2 bg-[#8b6f47] text-[#f5ede3] hover:bg-[#6b5540]"
        >
          <UploadCloud className="h-4 w-4" />
          アップロード
        </Button>
      </div>
    </div>
  );
}
